import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Send, Square, ChevronDown, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const SUPABASE_URL = "https://ewyfhrjexfrmcjhqzsmm.supabase.co";

type ToolEvent = {
  id: string;
  name: string;
  input: any;
  ok?: boolean;
  summary?: string;
  pending: boolean;
  result?: any;
};
type Citation = {
  id: string;
  source_id: string;
  title: string;
  axis: string;
  score: number;
  chunk_id?: string;
  extraction_id?: string;
  question_id?: string;
  preview?: string;
};
type StreamMsg = {
  role: "user" | "assistant";
  content: string;
  tool_events: ToolEvent[];
  citations: Citation[];
  statusPill?: string;
  done?: boolean;
  stopped?: boolean;
  error?: string;
  cost_usd?: number;
  run_ids?: string[];
  turns?: number;
  model?: string;
};

const TOOL_BADGE: Record<string, string> = {
  retrieve_hybrid: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  kg_lookup: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300",
  fred_lookup: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  remember_about_me: "bg-slate-100 text-slate-800 dark:bg-slate-700/40 dark:text-slate-200",
};

function inputSummary(input: any): string {
  if (!input) return "";
  if (typeof input === "string") return input.slice(0, 60);
  if (input.query) return `query: ${String(input.query).slice(0, 60)}`;
  if (input.entity) return `entity: ${input.entity}`;
  if (input.series_id) return `series: ${input.series_id}`;
  if (input.text) return String(input.text).slice(0, 60);
  return JSON.stringify(input).slice(0, 60);
}

export default function ResearchChatSession() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const qc = useQueryClient();
  const [input, setInput] = useState("");
  const [streamMsg, setStreamMsg] = useState<StreamMsg | null>(null);
  const [drawer, setDrawer] = useState<{ kind: "citation" | "tool"; payload: any } | null>(null);
  const [expandedTools, setExpandedTools] = useState<Record<string, boolean>>({});
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: session } = useQuery({
    queryKey: ["research_chat_session", sessionId],
    enabled: !!sessionId,
    queryFn: async () => {
      const { data } = await supabase
        .from("research_chat_sessions")
        .select("id, title, message_count")
        .eq("id", sessionId!)
        .maybeSingle();
      return data;
    },
  });

  const { data: persisted } = useQuery({
    queryKey: ["research_chat_messages", sessionId],
    enabled: !!sessionId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_chat_messages")
        .select("*")
        .eq("session_id", sessionId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const messages = useMemo<StreamMsg[]>(() => {
    const base: StreamMsg[] = (persisted ?? []).map((m: any) => ({
      role: m.role,
      content: m.content,
      tool_events: Array.isArray(m.tool_events) ? m.tool_events : [],
      citations: Array.isArray(m.citations) ? m.citations : [],
      done: true,
      cost_usd: m.cost_usd ?? undefined,
      run_ids: m.run_ids ?? undefined,
    }));
    return streamMsg ? [...base, streamMsg] : base;
  }, [persisted, streamMsg]);

  useEffect(() => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    });
  }, [messages]);

  function handleSSE(ev: string, data: any, acc: StreamMsg) {
    switch (ev) {
      case "status":
        acc.statusPill = data.detail ? `${data.phase}: ${data.detail}` : data.phase;
        if (data.phase === "model_switch" && data.detail) acc.model = data.detail;
        break;
      case "text_delta":
        acc.content += data.text || "";
        acc.statusPill = undefined;
        break;
      case "tool_use":
        acc.tool_events.push({
          id: crypto.randomUUID(),
          name: data.name,
          input: data.input,
          pending: true,
        });
        break;
      case "tool_result": {
        for (let i = acc.tool_events.length - 1; i >= 0; i--) {
          const t = acc.tool_events[i];
          if (t.name === data.name && t.pending) {
            t.pending = false;
            t.ok = data.ok;
            t.summary = data.summary;
            t.result = data.result;
            break;
          }
        }
        break;
      }
      case "citation":
        acc.citations.push({
          id: crypto.randomUUID(),
          source_id: data.source_id,
          title: data.title,
          axis: data.axis,
          score: data.score,
          chunk_id: data.chunk_id,
          extraction_id: data.extraction_id,
          question_id: data.question_id,
          preview: data.preview,
        });
        break;
      case "done":
        acc.done = true;
        acc.cost_usd = data.cost_usd;
        acc.run_ids = data.run_ids;
        acc.turns = data.turns;
        acc.statusPill = undefined;
        break;
      case "error":
        acc.error = data.message || "Stream error";
        acc.done = true;
        break;
    }
  }

  async function persistTurn(userText: string, assistant: StreamMsg) {
    if (!sessionId) return;
    await supabase.from("research_chat_messages").insert([
      { session_id: sessionId, role: "user", content: userText },
      {
        session_id: sessionId,
        role: "assistant",
        content: assistant.content + (assistant.stopped ? "\n\n_(stopped)_" : ""),
        tool_events: assistant.tool_events as any,
        citations: assistant.citations as any,
        run_ids: assistant.run_ids,
        cost_usd: assistant.cost_usd,
      },
    ]);
    const newCount = (session?.message_count ?? 0) + 2;
    const update: any = {
      last_message_at: new Date().toISOString(),
      message_count: newCount,
      updated_at: new Date().toISOString(),
    };
    if (session?.title === "New conversation") {
      update.title = userText.slice(0, 40);
    }
    await supabase.from("research_chat_sessions").update(update).eq("id", sessionId);
    qc.invalidateQueries({ queryKey: ["research_chat_messages", sessionId] });
    qc.invalidateQueries({ queryKey: ["research_chat_session", sessionId] });
    qc.invalidateQueries({ queryKey: ["research_chat_sessions"] });
  }

  async function send() {
    if (!input.trim() || !sessionId || streamMsg) return;
    const userText = input.trim();
    setInput("");
    const acc: StreamMsg = { role: "assistant", content: "", tool_events: [], citations: [] };
    setStreamMsg({ ...acc });

    // Optimistic user message render via persisted list will only appear after save.
    // For UX, show immediately:
    const optimisticUser: StreamMsg = { role: "user", content: userText, tool_events: [], citations: [], done: true };
    setStreamMsg(null);
    qc.setQueryData(["research_chat_messages", sessionId], (prev: any[] | undefined) => [
      ...(prev ?? []),
      { role: "user", content: userText, tool_events: [], citations: [], created_at: new Date().toISOString(), id: "tmp-" + Date.now() },
    ]);
    setStreamMsg({ ...acc });

    const ac = new AbortController();
    abortRef.current = ac;

    try {
      const { data: sessData } = await supabase.auth.getSession();
      const token = sessData.session?.access_token;
      if (!token) throw new Error("Not signed in");

      const history = (persisted ?? []).map((m: any) => ({ role: m.role, content: m.content }));
      const res = await fetch(`${SUPABASE_URL}/functions/v1/research-chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ query: userText, session_id: sessionId, history }),
        signal: ac.signal,
      });

      if (res.status === 402) {
        acc.error = "Monthly chat cost cap reached. The cap resets on the 1st.";
        acc.done = true;
        setStreamMsg({ ...acc });
        return;
      }
      if (res.status === 403) {
        acc.error = "You don't have access to the Research Hub. Contact an admin.";
        acc.done = true;
        setStreamMsg({ ...acc });
        return;
      }
      if (!res.ok || !res.body) {
        throw new Error(`Server error: ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const block = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const lines = block.split("\n");
          let event = "", data = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) event = ln.slice(6).trim();
            else if (ln.startsWith("data:")) data = ln.slice(5).trim();
          }
          if (!event || !data) continue;
          try {
            handleSSE(event, JSON.parse(data), acc);
          } catch {
            // ignore parse errors / unknown events
          }
          setStreamMsg({ ...acc, tool_events: [...acc.tool_events], citations: [...acc.citations] });
        }
      }
      if (!acc.done) {
        acc.error = "Stream ended unexpectedly. Send to retry.";
        acc.done = true;
      }
    } catch (e: any) {
      if (e?.name === "AbortError") {
        acc.stopped = true;
        acc.done = true;
      } else {
        acc.error = e?.message || "Network error";
        acc.done = true;
      }
    } finally {
      abortRef.current = null;
      setStreamMsg({ ...acc });
      // Persist (rollback optimistic user msg, persistence will reload from DB)
      try {
        await persistTurn(userText, acc);
      } catch (e: any) {
        toast.error("Failed to save message: " + e.message);
      }
      setStreamMsg(null);
    }
  }

  function stop() {
    abortRef.current?.abort();
  }

  if (!sessionId) return null;

  return (
    <div className="flex h-full min-h-0">
      <div className="flex-1 flex flex-col min-w-0">
        <div className="px-4 py-2 border-b text-sm font-medium truncate">
          {session?.title ?? "Conversation"}
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-10">
              Ask anything about the research corpus.
            </div>
          )}
          {messages.map((m, i) => (
            <MessageBubble
              key={i}
              msg={m}
              expandedTools={expandedTools}
              setExpandedTools={setExpandedTools}
              onOpenCitation={(c) => setDrawer({ kind: "citation", payload: c })}
              onOpenTool={(t) => setDrawer({ kind: "tool", payload: t })}
            />
          ))}
        </div>

        <div className="border-t p-3 space-y-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask the research corpus… (Cmd/Ctrl+Enter to send)"
            rows={2}
            className="resize-none max-h-48"
            disabled={!!streamMsg && !streamMsg.done}
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {input.length > 2000 ? `~${Math.round(input.length / 4)} tokens` : ""}
            </span>
            {streamMsg && !streamMsg.done ? (
              <Button size="sm" variant="destructive" onClick={stop}>
                <Square className="h-3.5 w-3.5 mr-1" /> Stop generating
              </Button>
            ) : (
              <Button size="sm" onClick={send} disabled={!input.trim()}>
                <Send className="h-3.5 w-3.5 mr-1" /> Send
              </Button>
            )}
          </div>
        </div>
      </div>

      {drawer && (
        <DetailDrawer drawer={drawer} onClose={() => setDrawer(null)} onSwitchCitation={(c) => setDrawer({ kind: "citation", payload: c })} />
      )}
    </div>
  );
}

function MessageBubble({
  msg, expandedTools, setExpandedTools, onOpenCitation, onOpenTool,
}: {
  msg: StreamMsg;
  expandedTools: Record<string, boolean>;
  setExpandedTools: (v: Record<string, boolean>) => void;
  onOpenCitation: (c: Citation) => void;
  onOpenTool: (t: ToolEvent) => void;
}) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="bg-muted rounded-lg px-3 py-2 text-sm max-w-[80%] whitespace-pre-wrap">
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2 max-w-[90%]">
      {msg.statusPill && (
        <Badge variant="outline" className="text-xs">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" /> {msg.statusPill}
        </Badge>
      )}
      {msg.tool_events.map((t) => {
        const expanded = expandedTools[t.id];
        return (
          <div key={t.id} className="border rounded-md bg-muted/30 text-xs">
            <button
              className="w-full flex items-center gap-2 px-2 py-1.5 text-left"
              onClick={() => setExpandedTools({ ...expandedTools, [t.id]: !expanded })}
            >
              {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              <Badge variant="outline" className={cn("border-0", TOOL_BADGE[t.name] ?? "bg-muted")}>{t.name}</Badge>
              <span className="text-muted-foreground truncate flex-1">{inputSummary(t.input)}</span>
              {t.pending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <span className={cn("font-medium", t.ok ? "text-green-600" : "text-destructive")}>
                  {t.ok ? t.summary || "ok" : "error"}
                </span>
              )}
            </button>
            {expanded && (
              <div className="px-2 pb-2 space-y-1">
                <button className="text-primary hover:underline text-xs" onClick={() => onOpenTool(t)}>
                  Open full result →
                </button>
              </div>
            )}
          </div>
        );
      })}
      {msg.content && (
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
          {!msg.done && <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-0.5" />}
        </div>
      )}
      {msg.citations.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {msg.citations.map((c) => (
            <button
              key={c.id}
              onClick={() => onOpenCitation(c)}
              className="text-[10px] px-1.5 py-0.5 rounded border bg-background hover:bg-muted flex items-center gap-1"
              title={`${c.axis} · score ${c.score?.toFixed?.(2) ?? c.score}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span className="truncate max-w-[180px]">{c.title}</span>
            </button>
          ))}
        </div>
      )}
      {msg.error && (
        <div className="text-xs text-destructive border border-destructive/40 bg-destructive/5 rounded px-2 py-1">
          {msg.error}
        </div>
      )}
      {msg.done && (msg.cost_usd != null || msg.turns != null) && (
        <div className="text-[10px] text-muted-foreground">
          {msg.cost_usd != null && <>${msg.cost_usd.toFixed(4)} · </>}
          {msg.turns != null && <>{msg.turns} turn{msg.turns === 1 ? "" : "s"}</>}
          {msg.model && <> · {msg.model}</>}
          {msg.stopped && <> · stopped</>}
        </div>
      )}
    </div>
  );
}

function DetailDrawer({
  drawer, onClose, onSwitchCitation,
}: {
  drawer: { kind: "citation" | "tool"; payload: any };
  onClose: () => void;
  onSwitchCitation: (c: Citation) => void;
}) {
  return (
    <aside className="w-[360px] shrink-0 border-l bg-background flex flex-col">
      <div className="flex items-center justify-between p-3 border-b">
        <span className="text-sm font-semibold">
          {drawer.kind === "citation" ? "Source" : `Tool: ${drawer.payload.name}`}
        </span>
        <button onClick={onClose} className="hover:bg-muted rounded p-1">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2 text-sm">
        {drawer.kind === "citation" ? (
          <CitationDetail c={drawer.payload} />
        ) : (
          <ToolDetail t={drawer.payload} onSwitchCitation={onSwitchCitation} />
        )}
      </div>
    </aside>
  );
}

function CitationDetail({ c }: { c: Citation }) {
  return (
    <>
      <div className="font-medium">{c.title}</div>
      <div className="flex gap-2 text-xs">
        <Badge variant="outline">axis: {c.axis}</Badge>
        <Badge variant="outline">score: {c.score?.toFixed?.(2) ?? c.score}</Badge>
      </div>
      {c.preview && (
        <div className="text-xs bg-muted/40 border rounded p-2 whitespace-pre-wrap">{c.preview}</div>
      )}
      <div className="text-xs text-muted-foreground space-y-1">
        <div>source_id: <code>{c.source_id}</code></div>
        {c.chunk_id && <div>chunk_id: <code>{c.chunk_id}</code></div>}
        {c.extraction_id && <div>extraction_id: <code>{c.extraction_id}</code></div>}
        {c.question_id && <div>question_id: <code>{c.question_id}</code></div>}
      </div>
      <Button
        size="sm"
        variant="outline"
        disabled
        title="Coming in Phase 5"
      >
        Open source
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => navigator.clipboard.writeText(c.source_id)}
      >
        Copy ID
      </Button>
    </>
  );
}

function ToolDetail({ t, onSwitchCitation }: { t: ToolEvent; onSwitchCitation: (c: Citation) => void }) {
  return (
    <>
      <div className="text-xs">
        <div className="font-medium mb-1">Input</div>
        <pre className="bg-muted/40 border rounded p-2 overflow-x-auto text-[10px]">
          {JSON.stringify(t.input, null, 2)}
        </pre>
      </div>
      <div className="text-xs">
        <div className="font-medium mb-1">Result</div>
        <div className={cn(t.ok ? "text-green-600" : "text-destructive")}>
          {t.summary || (t.ok ? "ok" : "error")}
        </div>
        {t.result && (
          <pre className="bg-muted/40 border rounded p-2 overflow-x-auto text-[10px] mt-1">
            {typeof t.result === "string" ? t.result : JSON.stringify(t.result, null, 2)}
          </pre>
        )}
      </div>
    </>
  );
}
